import React, { useCallback, useState, useEffect, useRef } from "react";
import useSelfpointEffect from "../common3/hooks/useSelfpointEffect";
import useDidMount from "../common3/hooks/useDidMount";
import { useDraggable } from "../common3/hooks/useDraggableScroll";
import {
  Wrapper,
  TableWrapper,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  StyledIcon,
  Search,
  NoDataWrapper,
  FilterWrapper,
  FilterBadge,
  FilterIcon,
  TopBar,
  PageSizeWrapper,
  Page,
  Reload,
  MoreWrapper,
  MultiSelectWrapper,
} from "./styled";
import moment from "moment";
import get from "lodash/get";
import produce from "immer";
import FormLabel from "../FormLabel";
import { injectIntl } from "react-intl";
import DotLoader from "../common3/Loaders/DotLoader";
import MoreDropDown from "./MoreDropDown";
import Checkbox from "./Checkbox/index";
import filterComponents from "./filterComponents";
import cols from "./columns";
import PaginationBar from "./PaginationBar";

const ColGroup = ({ columns }) => {
  const getColumntWidth = (width) => {
    switch (width) {
      case "xs":
        return "5% !important";
      case "s":
        return "10%";
      case "m":
        return "15%";
      case "l":
        return "25%";
      case "xl":
        return "50%";
      default:
        return null;
    }
  };

  return (
    <colgroup>
      {columns.map((c, i) => {
        return (
          <col key={i} span="1" style={{ width: getColumntWidth(c.width) }} />
        );
      })}
    </colgroup>
  );
};

const FilterButton = ({ index, filter, removeFilter }) => {
  if (typeof filter !== "string") {
    return null;
  }

  return (
    <FilterBadge>
      <FilterIcon
        data-index={index}
        onClick={removeFilter}
        className="fal fa-times-circle"
      />
      {filter}
    </FilterBadge>
  );
};

const SortIcon = ({ name, sort = {} }) => {
  const icon =
    sort[name] === 1 ? "fa fa-sort-alpha-up" : "fa fa-sort-alpha-down";

  return <StyledIcon data-name={name} selected={sort[name]} className={icon} />;
};

const Tickets = (props) => {
  const {
    extra = {},
    setUserPersistentProfile,
    id,
    userPersistentProfile,
    document,
    intl,
    dirty = {},
    runEffect,
    effectsLoading,
    permissions = [],
    user = {},
    effects = {},
  } = props;
  const {
    effects: _effect = {},
    hasPagination = false,
    paginationAbove = false,
    hasFilter = false,
    label = null,
    pageSize: configPageSize = 10,
    enablePageSize = false,
    multiSelect = false,
    dropDownLabel = undefined,
    actionButtons = [],
    enableComponentFilter,
    componentFilterId,
    deletePermissions = [],
    changeStatusPermissions = [],
    selectAllEnabled = false,
  } = extra;
  const { roles = [] } = user || {};
  const thisConfig = get(userPersistentProfile, [id], {});
  const {
    pageSize: _pageSize = +configPageSize,
    page: _page = 1,
    filter: _filter = "",
    filters: _filters = [],
    sort: _sort = {},
  } = thisConfig;
  const [pageSize, setPageSize] = useState(_pageSize);
  const [page, setPage] = useState(_page);
  const [_reload, setReload] = useState(null);
  const [filter, setFilter] = useState(_filter);
  const [filters, setFilters] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [isTotalSelected, setIsTotalSelected] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sort, setSort] = useState({});
  const tableWrapperRef = useRef();
  const didMount = useDidMount();
  const { events } = useDraggable(tableWrapperRef, {
    isMounted: didMount,
  });
  const effectConfig = get(extra, "effects", []);
  const columns = get(effectConfig, "[0].columns", []);
  const formDataDirty = dirty["formData"];

  let componentFilter = enableComponentFilter
    ? document && document[componentFilterId]
    : null;

  const hasDeletePermission =
    permissions.includes("admin") ||
    roles?.some(
      (role) =>
        deletePermissions.includes(role) ||
        deletePermissions.includes(role.toString())
    );

  const hasStatusChangePermission =
    permissions.includes("admin") ||
    roles?.some(
      (role) =>
        changeStatusPermissions.includes(role) ||
        changeStatusPermissions.includes(role.toString())
    );

  const [, effect] = useSelfpointEffect({
    ...props,
    context: {
      page: page,
      pageSize: pageSize,
      sort: sort,
      filter: filter,
      filters: filters,
      componentFilter,
      includeAllIds: multiSelect && selectAllEnabled,
    },
    watchArray: [
      page,
      sort,
      pageSize,
      filter,
      filters,
      formDataDirty,
      _reload,
      document,
      componentFilter,
    ],
  });

  useEffect(() => {
    if (didMount) {
      setUserPersistentProfile({
        [id]: {
          page,
          sort,
          pageSize,
          filter,
          filters,
        },
      });
    }
  }, [page, sort, pageSize, filter, filters]);

  useEffect(() => {
    if (didMount) {
      if (!effectsLoading["deleteTicket"]) {
        reload();
      }
    }
  }, [effectsLoading["deleteTicket"]]);

  useEffect(() => {
    if (didMount) {
      if (!effectsLoading["changeStatusOfSelectedTickets"]) {
        reload();
      }
    }
  }, [effectsLoading["changeStatusOfSelectedTickets"]]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    if (!hasStatusChangePermission) {
      return;
    }

    runEffect({
      source: "Components/Tickets/selectedTicketsHasSameFormAndStatus",
      context: { formDataIds: selected },
      effectId: "ticketsHasSameFormAndStatus",
    });
  }, [selected]);

  const onSortClick = useCallback((e) => {
    const name = get(e, "currentTarget.dataset.name", null);

    setSort(
      produce((draft) => {
        if (draft[name]) {
          return {
            [name]: draft[name] * -1,
          };
        } else {
          return {
            [name]: -1,
          };
        }
      })
    );
  });

  const runActionButton = (effects) => {
    if (!selected.length || !effects || !effects.length) {
      return;
    }

    runEffect({
      source: "ActionButton/run",
      effectId: "runActionButton",
      context: {
        selected,
        effectsToRun: effects,
        sort,
      },
    });
  };

  const removeSelected = useCallback(() => {
    if (!selected.length) {
      return;
    }

    runEffect({
      source: "Ticket/delete",
      effectId: "deleteTicket",
      context: {
        selected,
        deletePermissions,
      },
    });
    setSelected([]);
  }, [selected]);

  const changeStatusOfSelected = ({ connection }) => {
    if (!selected.length) {
      return;
    }

    runEffect({
      source: "Components/Tickets/changeStatusOfSelectedTickets",
      effectId: "changeStatusOfSelectedTickets",
      context: {
        selectedTickets: selected,
        connection,
        changeStatusPermissions,
      },
    });
  };

  const checkForEnter = useCallback(
    (e) => {
      if (e.key === "Enter") {
        setFilters(
          produce((draft) => {
            draft.push(filter);
          })
        );
        setFilter("");
      }
    },
    [filter]
  );

  const removeFilter = useCallback(
    (e) => {
      const index = get(e, "currentTarget.dataset.index");

      setFilters(
        produce((draft) => {
          draft.splice(index, 1);
        })
      );
    },
    [filters]
  );

  const extractLabelKey = (label) => {
    if (label) {
      const _array = label.split(".");
      const _label = _array[0];
      const _field = _array[1];
      return [_label, _field];
    } else {
      return [null, null];
    }
  };

  const reload = useCallback(() => {
    setReload(new Date());
  });

  if (!effect) {
    return <DotLoader />;
  }

  const { data, totalCount, allIds } = effect;

  if (!columns) {
    return "no columns";
  }

  if (!Array.isArray(data)) {
    return <div>Corrupt</div>;
  }

  moment.locale("sv");

  const onSelectAll = () => {
    const ids = data.map(({ _id = "" }) => _id);

    if (isTotalSelected) {
      setIsTotalSelected(false);
    }

    if (ids.some((id) => !selected.includes(id))) {
      setSelected(ids);
      setAllSelected(true);

      return;
    }

    setSelected([]);
    setAllSelected(false);
  };

  const onSelectTotal = () => {
    if (isTotalSelected) {
      if (allSelected) {
        setAllSelected(false);
      }

      setSelected([]);
    } else {
      setSelected(allIds);
    }

    setIsTotalSelected((prev) => !prev);
  };

  const onSelect = (rowId) => {
    if (selected.includes(rowId)) {
      setSelected(selected.filter((id) => id !== rowId));
      setAllSelected(false);

      return;
    }

    setSelected([...selected, rowId]);

    const ids = data.map(({ _id = "" }) => _id);

    if (ids.every((id) => [...selected, rowId].includes(id))) {
      setAllSelected(true);
    }
  };

  const shouldRenderSelectAllCheckbox =
    selectAllEnabled && totalCount > pageSize;
  const shouldDisplayMoreDropdown = selected.length > 0 || isTotalSelected;

  return (
    <React.Fragment>
      <FormLabel label={label} />
      <Wrapper>
        {hasPagination && paginationAbove && (
          <PaginationBar
            pageSize={pageSize}
            page={page}
            setPage={setPage}
            totalCount={totalCount}
          />
        )}
        <TopBar>
          {hasFilter && (
            <FilterWrapper>
              <Search
                className="sp-tickets-filter"
                placeholder="Filtrera"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={checkForEnter}
              />
              {filters.map((filter, i) => {
                return (
                  <FilterButton
                    key={`filter-${i}`}
                    filter={filter}
                    index={i}
                    removeFilter={removeFilter}
                  />
                );
              })}
              <div>
                {columns.map((col) => {
                  if (!col.filter) {
                    return null;
                  }

                  const path = col.key ? col.value + "." + col.key : col.value;

                  const FilterComponent = filterComponents[col.type];

                  return (
                    <FilterComponent
                      key={col.key}
                      label={col.label}
                      path={path}
                      setFilters={setFilters}
                    />
                  );
                })}
              </div>
            </FilterWrapper>
          )}
          {shouldDisplayMoreDropdown && (
            <MoreWrapper>
              <MoreDropDown
                permissions={permissions}
                numOfSelected={selected.length}
                runActionButton={runActionButton}
                removeSelected={removeSelected}
                dropDownLabel={dropDownLabel}
                actionButtons={actionButtons}
                intl={intl}
                hasDeletePermission={hasDeletePermission}
                hasStatusChangePermission={hasStatusChangePermission}
                changeStatusOfSelected={changeStatusOfSelected}
                runEffect={runEffect}
                effects={effects}
                selected={selected}
              />
            </MoreWrapper>
          )}
          {enablePageSize && (
            <>
              <Reload
                onClick={reload}
                shouldDisplayMoreDropdown={shouldDisplayMoreDropdown}
              >
                {intl.formatMessage({ id: "Reload" })}
              </Reload>
              <PageSizeWrapper>
                {[5, 10, 15, 25, 50, 100].map((size) => {
                  return (
                    <Page
                      key={size}
                      onClick={() => {
                        setPageSize(size);
                        setPage(1);
                      }}
                      selected={size === pageSize}
                    >
                      {size}
                    </Page>
                  );
                })}
              </PageSizeWrapper>
            </>
          )}
        </TopBar>
        <TableWrapper ref={tableWrapperRef} {...events}>
          <Table className="sp-tickets-table">
            <ColGroup columns={columns} />
            <THead className="sp-tickets-thead">
              <TR className="sp-tickets-thead-tr">
                {multiSelect && (
                  <MultiSelectWrapper
                    shouldRenderSelectAllCheckbox={
                      shouldRenderSelectAllCheckbox
                    }
                  >
                    <TH>
                      <Checkbox
                        checked={allSelected}
                        onSelectAll={onSelectAll}
                        onClick={onSelectAll}
                        value={false}
                        ariaLabel="Select all"
                        label={
                          shouldRenderSelectAllCheckbox
                            ? "Tickets.selectAllOnPage"
                            : ""
                        }
                      />
                    </TH>
                    {shouldRenderSelectAllCheckbox && (
                      <TH>
                        <Checkbox
                          checked={isTotalSelected}
                          onSelectAll={onSelectTotal}
                          onClick={onSelectTotal}
                          value={false}
                          ariaLabel={`Select all ${totalCount}`}
                          label={intl.formatMessage(
                            { id: "Tickets.selectAllTotal" },
                            { totalCount }
                          )}
                        />
                      </TH>
                    )}
                  </MultiSelectWrapper>
                )}
                {columns.map((col, i) => {
                  const [_label, _field] = extractLabelKey(col.label);

                  if (col.display === true) {
                    if (col.type === "arrayObject") {
                      const numOfItems = data.reduce((acc, row) => {
                        //Extra check to make sure we have a value (Not undefined)
                        if (row[col.value] && row[col.value].length > acc) {
                          return row[col.value].length;
                        }

                        return acc;
                      }, 0);

                      if (numOfItems > 1) {
                        return Array(numOfItems)
                          .fill("")
                          .map((c, colIdx) => (
                            <TH
                              className="sp-tickets-th"
                              sortable={col.sortable}
                              data-name={col.value + "." + col.key}
                              key={"th" + i + "-" + colIdx}
                              onClick={col.sortable ? onSortClick : () => {}}
                            >
                              {col.type !== "notifications" &&
                                _label + " (" + (colIdx + 1) + ")"}{" "}
                              {col.sortable && (
                                <SortIcon name={col.value} sort={sort} />
                              )}
                            </TH>
                          ));
                      } else {
                        return (
                          <TH
                            className="sp-tickets-th"
                            sortable={col.sortable}
                            data-name={col.value + "." + col.key}
                            key={"th" + i}
                            onClick={col.sortable ? onSortClick : () => {}}
                          >
                            {col.type !== "notifications" && _label}{" "}
                            {col.sortable && (
                              <SortIcon name={col.value} sort={sort} />
                            )}
                          </TH>
                        );
                      }
                    }
                    return (
                      <TH
                        className="sp-tickets-th"
                        sortable={col.sortable}
                        data-name={
                          col.key ? col.value + "." + col.key : col.value
                        }
                        key={"th" + i}
                        onClick={col.sortable ? onSortClick : () => {}}
                      >
                        {col.type !== "notifications" && _label}{" "}
                        {col.sortable && (
                          <SortIcon name={col.value} sort={sort} />
                        )}
                      </TH>
                    );
                  } else {
                    return null;
                  }
                })}
              </TR>
            </THead>
            <TBody className="sp-tickets-tbody">
              {data.map((row) => {
                return (
                  <TR key={`${row._id}-tr`} className="sp-tickets-tr">
                    {multiSelect && (
                      <TD>
                        <Checkbox
                          checked={selected.includes(row._id)}
                          onSelect={onSelect}
                          rowId={row._id}
                          onClick={() => onSelect(row._id)}
                          ariaLabel="Select"
                        />
                      </TD>
                    )}
                    {columns.map((col, i) => {
                      const [_label, _field] = extractLabelKey(col.label);

                      if (col.display) {
                        const Comp =
                          cols[
                            col.value === "details"
                              ? "link"
                              : col.type === "object" ||
                                col.type === "arrayObject"
                              ? "string"
                              : col.type
                              ? col.type
                              : "string"
                          ];

                        if (col.type === "arrayObject") {
                          const numOfItems = data.reduce((acc, row) => {
                            //Extra check to make sure we have a value (Not undefined)
                            if (row[col.value] && row[col.value].length > acc) {
                              return row[col.value].length;
                            }

                            return acc;
                          }, 0);

                          if (numOfItems === 0) {
                            return (
                              <TD
                                data-label={_label}
                                className="sp-tickets-td"
                                key={`${row._id}-col-${i}`}
                              />
                            );
                          }

                          return Array(numOfItems)
                            .fill("")
                            .map((c, colIdx) => {
                              //Extra check to make sure we have a value (Not undefined)
                              if (
                                row &&
                                row[col.value] &&
                                row[col.value][colIdx]
                              ) {
                                return (
                                  <TD
                                    key={`${row._id}-col-${i}-${colIdx}`}
                                    data-label={_label}
                                    className="sp-tickets-td"
                                  >
                                    <Comp
                                      row={row}
                                      intl={intl}
                                      name={
                                        col.value + "." + colIdx + "." + col.key
                                      }
                                      extra={extra}
                                      keyValue={_field}
                                    />
                                  </TD>
                                );
                              }

                              return (
                                <TD
                                  key={`${row._id}-col-${i}-${colIdx}`}
                                  data-label={_label}
                                  className="sp-tickets-td"
                                />
                              );
                            });
                        }

                        //If we have no value on createdByName we set it to anonmyous
                        const dataRow = { ...row };
                        dataRow.createdByName = dataRow.createdByName
                          ? dataRow.createdByName
                          : "Anonymous";
                        return (
                          <TD
                            key={`${row._id}-col-${i}`}
                            data-label={_label}
                            className="sp-tickets-td"
                          >
                            <Comp
                              row={dataRow}
                              intl={intl}
                              name={
                                col.key ? col.value + "." + col.key : col.value
                              }
                              extra={extra}
                              keyValue={_field}
                            />
                          </TD>
                        );
                      } else {
                        return null;
                      }
                    })}
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </TableWrapper>
        {filter && data.length === 0 && (
          <NoDataWrapper>
            {intl.formatMessage({ id: "Tickets.NoResult" })}
          </NoDataWrapper>
        )}

        {hasPagination && (
          <PaginationBar
            pageSize={pageSize}
            page={page}
            setPage={setPage}
            totalCount={totalCount}
          />
        )}
      </Wrapper>
    </React.Fragment>
  );
};

export default injectIntl(Tickets);
